import React, { useState, useEffect } from 'react';

const ManageHomepage = () => {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const token = localStorage.getItem('adminToken');

  const emptySlide = {
    title: '',
    subtitle: '',
    link: '/#book-appointment',
    image: '',
    order: 0,
    active: true
  };

  const [formData, setFormData] = useState(emptySlide);

  useEffect(() => {
    fetchSlides();
  }, []);

  const fetchSlides = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/content/slides/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setSlides(data.slides || []);
    } catch (err) {
      console.error('Failed to fetch slides:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editing 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/content/slides/${editing}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/admin/content/slides`;
      
      const res = await fetch(url, {
        method: editing ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        fetchSlides();
        setShowForm(false);
        setEditing(null);
        setFormData(emptySlide);
      }
    } catch (err) {
      console.error('Failed to save slide:', err);
    }
  };

  const handleEdit = (slide) => {
    setFormData(slide);
    setEditing(slide.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this slide?')) return;
    
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/content/slides/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchSlides();
    } catch (err) {
      console.error('Failed to delete slide:', err);
    }
  };

  const toggleActive = async (slide) => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/content/slides/${slide.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ...slide, active: !slide.active })
      });
      fetchSlides();
    } catch (err) {
      console.error('Failed to toggle slide:', err);
    }
  };

  if (loading) return <div className="text-center py-8">Loading slides...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">Manage Homepage</h2>
          <p className="text-gray-600 text-sm mt-1">Manage hero slider and homepage content</p>
        </div>
        <button
          onClick={() => { setShowForm(true); setEditing(null); setFormData(emptySlide); }}
          className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition"
        >
          + Add New Slide
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-xl">
            <h3 className="text-lg font-semibold mb-4">
              {editing ? 'Edit Slide' : 'Create New Slide'}
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
                  placeholder="e.g., Beautiful Smiles Start Here"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle *</label>
                <input
                  type="text"
                  value={formData.subtitle}
                  onChange={(e) => setFormData({...formData, subtitle: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
                  placeholder="e.g., Expert dental care for the whole family"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Button Link</label>
                <input
                  type="text"
                  value={formData.link}
                  onChange={(e) => setFormData({...formData, link: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
                  placeholder="e.g., /#book-appointment or /specials"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Image URL *</label>
                <input
                  type="text"
                  value={formData.image}
                  onChange={(e) => setFormData({...formData, image: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
                  placeholder="https://..."
                  required
                />
                {formData.image && (
                  <img src={formData.image} alt="Preview" className="mt-2 h-24 rounded-lg object-cover" />
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Order</label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({...formData, order: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                </div>

                <div className="flex items-center pt-6">
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData({...formData, active: e.target.checked})}
                    className="mr-2 h-4 w-4"
                  />
                  <label className="text-sm font-medium text-gray-700">Active</label>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setEditing(null); }}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
                >
                  {editing ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Hero Slides Section */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Hero Slider</h3>
        
        {slides.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No slides yet. The homepage will use default slides.
          </div>
        ) : (
          <div className="grid gap-4">
            {slides.map((slide) => (
              <div key={slide.id} className="flex items-center gap-4 p-4 border rounded-lg hover:bg-gray-50">
                {slide.image && (
                  <img src={slide.image} alt={slide.title} className="w-24 h-16 object-cover rounded" />
                )}
                <div className="flex-1">
                  <div className="font-medium">{slide.title}</div>
                  <div className="text-sm text-gray-500">{slide.subtitle}</div>
                </div>
                <div className="text-sm text-gray-400">Order: {slide.order}</div>
                <button
                  onClick={() => toggleActive(slide)}
                  className={`px-2 py-1 rounded text-xs font-medium ${slide.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}
                >
                  {slide.active ? 'Active' : 'Inactive'}
                </button>
                <button
                  onClick={() => handleEdit(slide)}
                  className="text-blue-600 hover:underline"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(slide.id)}
                  className="text-red-600 hover:underline"
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <h4 className="font-medium text-blue-800">How Hero Slides Work</h4>
        <ul className="text-sm text-blue-700 mt-2 space-y-1">
          <li>• Slides are displayed in order from lowest to highest order number</li>
          <li>• Only active slides are shown on the homepage</li>
          <li>• If no slides are configured, default slides will be displayed</li>
          <li>• Recommended image size: 1920x800 pixels</li>
        </ul>
      </div>
    </div>
  );
};

export default ManageHomepage;
